import React from "react";
import { SurveySegment, SelectionSummary, ThemeQuestionData } from "../../../amorg-common";
import { getSurveyComments } from "../../../api/surveyClientApi";
import { Button } from "../../../designSystem/button/Button";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ListRow } from "../../../designSystem/compositeComponents/ListRow";
import { AnswerRatioBarGraph } from "./AnswerRatioBarGraph";
import { GroupedAnswerRatioBarGraph } from "./GroupedAnswerRatioBarGraph";
import { IndicatorList } from "./IndicatorList";
import styled from "styled-components";
import { colors } from "../../../designSystem/colors/color";
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { Icon } from "../../../designSystem/icons/Icons";
import { RadialPctChart } from "../../../designSystem/graphs/RadialPctChart";


type QuestionPanelProps = {
    surveyId: string,
    siteId: string,
    details: ThemeQuestionData,
    segmentFilter: SurveySegment[][],
    showCommentsButton: boolean,
    showDetails?: boolean,
}
export function QuestionPanel(props: QuestionPanelProps): JSX.Element {
    const [comments, setComments] = React.useState<{ text: string, selection: number }[] | undefined>(undefined);
    const [showDetailedGraph, setShowDetailedGraph] = React.useState(false);

    React.useEffect(() => {
        const update = async () => {
            setShowDetailedGraph(props.showDetails || false);
            if(comments !== undefined)
            {
                const comments = await getSurveyComments(props.siteId, props.surveyId, props.segmentFilter, props.details.themeIndex, props.details.themeQuestionIndex);
                setComments(comments);
            }
        };
        update();
    }, [props.showDetails, props.segmentFilter]);

    async function handleShowComments() {
        const comments = await getSurveyComments(props.siteId, props.surveyId, props.segmentFilter, props.details.themeIndex, props.details.themeQuestionIndex);
        setComments(comments);
    }

    const data:SelectionSummary = props.details.questionReport.selections.data;

    return (
        <div>
            <ListRow styleVariant="neutral" sizeVariant="slim" level="level2">
                    <Flex justifyContent="space-between">
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:4, flexGrow:1}}>

                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <QuestionNumber>#{(props.details.surveyQuestionIndex + 1)}</QuestionNumber>
                                <RadialPctChart dim="60px" fontSize="8" pct={props.details.questionReport.selections.agreePlusPct} />
                                <Flex alignItems="center" gap={2}>
                                    {props.details.questionReport.commentCount}
                                    <Icon size="x-small" name={'interface_glyph_comment'} />
                                </Flex>
                            </div>

                            <div style={{display:'flex', flexDirection:'column', flexGrow:1, gap:8, maxWidth:600}}>
                                <QuestionTitle>{props.details.surveyQuestion.title}</QuestionTitle>
                                <div>
                                    {showDetailedGraph
                                        ? <AnswerRatioBarGraph data={data} legend='none' showValueText />
                                        : <GroupedAnswerRatioBarGraph data={data} legend='none' showValueText />
                                    }
                                </div>
                                <IndicatorList indicators={props.details.surveyQuestion.indicators} />
                            </div>

                        </div>
                        <div style={{alignSelf:'center'}}>
                            <Button variant='text'
                                disabled={props.details.questionReport.commentCount === 0}
                                icon={ comments===undefined ? 'arrows_chevron_chevronDown' : 'arrows_chevron_chevronUp' } 
                                onClick={() => comments===undefined ? handleShowComments() : setComments(undefined) } />
                        </div>
                                
                    </Flex>
            </ListRow>
            <AnimatedCollapsibleContent open={comments !== undefined}>
                {comments !== undefined &&
                    <>
                        {comments.sort((a, b) => a.selection - b.selection).map((c,i) => 
                            <ListRow key={i} styleVariant="neutral" sizeVariant="slim" level="level3">
                                    <Flex gap={5}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            color:'white',
                                            backgroundColor:colors.graphs.survey.getAnswerAverageColor(c.selection),
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            flexShrink:0
                                            }}>{c.selection}</div>
                                        <div>{c.text}</div>
                                    </Flex>
                            </ListRow>
                        )}
                    </>
                }
            </AnimatedCollapsibleContent>
        </div>
    );
}

const QuestionNumber = styled.div`
    color: rgba(191,191,191,1);
    font-size:1rem;
    font-weight: 700;
    line-height: 1.5rem;
    margin-right: 5px;
    flex:0;
`;

const QuestionTitle = styled.div`
    overflow-wrap: break-word;
    white-space: pre-wrap; 
    font-weight: 600;
`;